import React, { useState } from "react";
import {
  Calendar,
  Landmark,
  Zap,
  RefreshCw,
  ClipboardList,
  Flag,
  PenTool,
} from "lucide-react";

// Legislative Timeline Component
const LegislativeTimeline = ({ isDarkMode }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const timelineEvents = [
    {
      date: "Jan 12, 2026",
      legislativeDay: "Day 1",
      event: "Session Begins",
      description: "Official start of the 40-day session",
      icon: Landmark,
      color: "blue",
      isPast: false,
      isCurrent: false,
    },
    {
      date: "Feb 11, 2026",
      legislativeDay: "Day 20",
      event: "Mid-Session Point",
      description: "Rough halfway through the session",
      icon: Zap,
      color: "yellow",
      isPast: false,
      isCurrent: false,
    },
    {
      date: "Mar 6, 2026",
      legislativeDay: "Day 28",
      event: "Crossover Day",
      description: "Bills must pass originating chamber to move forward",
      icon: RefreshCw,
      color: "orange",
      isPast: false,
      isCurrent: false,
      isImportant: true,
    },
    {
      date: "Mar 16, 2026",
      legislativeDay: "Day 33",
      event: "Final Committee Deadline",
      description: "Last day committees can report bills",
      icon: ClipboardList,
      color: "red",
      isPast: false,
      isCurrent: false,
      isImportant: true,
    },
    {
      date: "Apr 2, 2026",
      legislativeDay: "Day 40",
      event: "Sine Die (Session Ends)",
      description: "Final day of the regular session",
      icon: Flag,
      color: "purple",
      isPast: false,
      isCurrent: false,
      isImportant: true,
    },
    {
      date: "May 12, 2026",
      legislativeDay: "~40 days post",
      event: "Governor's Deadline",
      description: "Governor has 40 days post Sine Die to act on bills",
      icon: PenTool,
      color: "green",
      isPast: false,
      isCurrent: false,
      isImportant: true,
    },
  ];

  // Calculate current date status
  const now = new Date();
  const currentEvents = timelineEvents.map((event) => {
    const eventDate = new Date(event.date);
    return {
      ...event,
      isPast: eventDate < now,
      isCurrent: Math.abs(eventDate - now) < 7 * 24 * 60 * 60 * 1000, // Within a week
    };
  });

  const getEventColors = (event) => {
    const colorMap = {
      blue: {
        bg: isDarkMode ? "bg-blue-900/30" : "bg-blue-50",
        border: isDarkMode ? "border-blue-700" : "border-blue-200",
        text: isDarkMode ? "text-blue-300" : "text-blue-800",
        dot: "bg-blue-500",
      },
      yellow: {
        bg: isDarkMode ? "bg-yellow-900/30" : "bg-yellow-50",
        border: isDarkMode ? "border-yellow-700" : "border-yellow-200",
        text: isDarkMode ? "text-yellow-300" : "text-yellow-800",
        dot: "bg-yellow-500",
      },
      orange: {
        bg: isDarkMode ? "bg-orange-900/30" : "bg-orange-50",
        border: isDarkMode ? "border-orange-700" : "border-orange-200",
        text: isDarkMode ? "text-orange-300" : "text-orange-800",
        dot: "bg-orange-500",
      },
      red: {
        bg: isDarkMode ? "bg-red-900/30" : "bg-red-50",
        border: isDarkMode ? "border-red-700" : "border-red-200",
        text: isDarkMode ? "text-red-300" : "text-red-800",
        dot: "bg-red-500",
      },
      purple: {
        bg: isDarkMode ? "bg-purple-900/30" : "bg-purple-50",
        border: isDarkMode ? "border-purple-700" : "border-purple-200",
        text: isDarkMode ? "text-purple-300" : "text-purple-800",
        dot: "bg-purple-500",
      },
      green: {
        bg: isDarkMode ? "bg-green-900/30" : "bg-green-50",
        border: isDarkMode ? "border-green-700" : "border-green-200",
        text: isDarkMode ? "text-green-300" : "text-green-800",
        dot: "bg-green-500",
      },
    };

    const colors = colorMap[event.color];

    if (event.isPast) {
      return {
        ...colors,
        dot: colors.dot + " opacity-75",
      };
    }

    if (event.isCurrent) {
      return {
        ...colors,
        border: colors.border + " ring-2 ring-blue-400/50",
      };
    }

    return colors;
  };

  // Get next upcoming event
  const getNextEvent = () => {
    const now = new Date();
    return currentEvents.find((event) => {
      const eventDate = new Date(event.date);
      return eventDate > now;
    });
  };

  const nextEvent = getNextEvent();

  return (
    <div
      className={`${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-xl shadow-lg border ${isDarkMode ? "border-gray-700" : "border-gray-200"} overflow-hidden transition-all duration-300 ${isExpanded ? "shadow-xl" : ""}`}
    >
      {/* Compact Header - Always Visible */}
      <div
        className={`${isDarkMode ? "bg-gradient-to-r from-red-900/20 to-blue-900/20" : "bg-gradient-to-r from-red-50 to-blue-50"} px-4 py-3 cursor-pointer hover:bg-opacity-80 transition-all duration-200`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full ${isDarkMode ? "bg-gradient-to-r from-red-600 to-blue-600" : "bg-gradient-to-r from-red-500 to-blue-500"}`}
            >
              <Calendar className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <h3
                className={`text-base font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                2026 Georgia Legislative Session
              </h3>
              {!isExpanded && (
                <div className="space-y-2 mt-1">
                  {/* Key dates info */}
                  <div className="flex items-center space-x-4 text-xs">
                    {nextEvent && (
                      <div className="flex items-center space-x-1">
                        <span
                          className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          Up next:
                        </span>
                        <span
                          className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {nextEvent.event}
                        </span>
                        <span
                          className={`${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                        >
                          ({nextEvent.date})
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Mini Timeline Visual */}
                  <div className="flex items-center space-x-1">
                    {currentEvents.map((event, idx) => {
                      const now = new Date();
                      const eventDate = new Date(event.date);
                      const isPastEvent = eventDate < now;
                      const isCurrentEvent =
                        Math.abs(eventDate - now) < 7 * 24 * 60 * 60 * 1000;

                      // Simple dot colors
                      const getEventDotColor = () => {
                        if (isPastEvent) {
                          return isDarkMode ? "bg-green-500" : "bg-green-600";
                        } else if (isCurrentEvent) {
                          return "bg-blue-500";
                        } else {
                          return isDarkMode ? "bg-gray-500" : "bg-gray-400";
                        }
                      };

                      return (
                        <div key={idx} className="flex items-center">
                          {/* Event dot */}
                          <div
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${getEventDotColor()}`}
                            title={`${event.event} - ${event.date}`}
                          />
                          {/* Connecting line (except for last item) */}
                          {idx < currentEvents.length - 1 && (
                            <div
                              className={`w-4 h-0.5 ${
                                isPastEvent
                                  ? "bg-green-400" // Completed path - green
                                  : isCurrentEvent
                                    ? "bg-blue-400" // Current path - blue
                                    : "bg-gray-500" // Future path - gray
                              }`}
                            ></div>
                          )}
                        </div>
                      );
                    })}
                    <div
                      className={`ml-2 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Jan 12 - Apr 2
                    </div>
                  </div>
                </div>
              )}
              {isExpanded && (
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  40-day regular session timeline
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {!isExpanded && nextEvent && (
              <div
                className={`px-2 py-1 rounded text-xs font-medium ${
                  isDarkMode
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {(() => {
                  const eventDate = new Date(nextEvent.date);
                  const today = new Date();
                  const diffTime = eventDate - today;
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  return diffDays <= 0 ? "Today" : diffDays === 1 ? "Tomorrow" : `in ${diffDays} days`;
                })()}
              </div>
            )}
            <div
              className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
            >
              <svg
                className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Timeline */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          <div className="p-6 animate-in slide-in-from-top-2 duration-300">
            <div className="overflow-x-auto overflow-y-visible pb-2">
              <div
                className="flex flex-nowrap items-start relative z-10 pt-2"
                style={{ minWidth: "max-content" }}
              >
                {currentEvents.map((event, idx) => {
                  const colors = getEventColors(event);
                  const isLast = idx === currentEvents.length - 1;
                  return (
                    <div key={idx} className="flex items-start flex-shrink-0">
                      {/* Event column */}
                      <div className="flex flex-col items-center min-w-[130px] max-w-[160px]">
                        {/* Icon */}
                        <div
                          className={`w-12 h-12 rounded-full border-2 flex items-center justify-center text-white mb-2 ${colors.dot} ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                        >
                          {React.createElement(event.icon, {
                            className: "w-5 h-5",
                          })}
                        </div>
                        {/* Event name */}
                        <div
                          className={`text-sm font-semibold mb-0.5 text-center ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {event.event}
                        </div>
                        {/* Date */}
                        <div
                          className={`text-xs text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {event.date}
                        </div>
                        <div
                          className={`text-xs text-center ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                        >
                          {event.legislativeDay}
                        </div>
                      </div>
                      {/* Connecting line to next event */}
                      {!isLast && (
                        <div
                          className={`h-0.5 mt-7 mx-2 flex-shrink-0 ${
                            event.isPast
                              ? "bg-green-400"
                              : isDarkMode
                                ? "bg-gray-600"
                                : "bg-gray-300"
                          }`}
                          style={{ width: "48px" }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegislativeTimeline;
